import dayjs from "dayjs";
import { supabaseClient } from "supabase";
import { PublishedState } from "types";

export type UseQueryEventsParams = {
  title?: string;
  isPaid?: boolean;
  forChildren?: boolean;
  startDate: Date | null;
  endDate: Date | null;
  page: number;
  perPage: number;
  publishedState: PublishedState;
  isMunicipality?: boolean;
  userUid?: string;
  madeByOrganizer?: boolean;
};

export const queryEvents = async ({
  title,
  isPaid,
  forChildren,
  startDate,
  endDate,
  page,
  perPage,
  publishedState,
  isMunicipality,
  userUid,
  madeByOrganizer,
}: UseQueryEventsParams) => {
  let querySelect;
  if (isMunicipality === undefined && userUid === undefined) {
    querySelect =
      "*,organizers(name),venues(name),bookmarks(count),new_views:views(count)";
  } else {
    querySelect =
      "*,organizers!inner(name),venues(name),bookmarks(count),new_views:views(count)";
  }
  let query = supabaseClient.from("events").select(querySelect, {
    // "estimated" lets Postgres skip the COUNT(*) OVER() window count, so LIMIT
    // applies before the per-row bookmarks/views aggregations — without this the
    // archived tab (every past event) computes those aggregates over all of
    // history and times out. Pagination total is approximate for large sets.
    count: "estimated",
  });

  const { data: authUserData } = await supabaseClient.auth.getUser();
  const user = await supabaseClient
    .from("users")
    .select("*")
    .eq("auth_uid", authUserData.user?.id || "")
    .single();

  if (user.data.organizer_id && user.data.organizer_id !== null) {
    query = query.eq("organizer_id", user.data.organizer_id);
  }

  if (title) {
    query = query.ilike("title", `%${title}%`);
  }

  if (isPaid !== undefined) {
    if (isPaid) {
      query = query.not("price", "is", null).neq("price", "");
    } else {
      query = query.or("price.is.null,price.eq.''");
    }
  }

  if (forChildren) {
    query = query.eq("for_children", forChildren);
  }

  if (startDate) {
    query = query.gte("start_time", dayjs(startDate).subtract(1).toISOString());
  }
  if (endDate) {
    query = query.lte("start_time", dayjs(endDate).add(1, "day").toISOString());
  }

  if (publishedState === "published") {
    query = query.eq("published", true).gte("start_time", dayjs());
  }

  if (publishedState === "draft") {
    query = query.eq("published", false);
  }

  if (publishedState === "archived") {
    query = query.lt("start_time", dayjs());
  }

  if (isMunicipality !== undefined) {
    query = query.eq("organizers.is_municipality", isMunicipality);
  }

  if (userUid) {
    query = query.eq("organizers.uid", userUid);
  }

  if (madeByOrganizer) {
    query = query.eq("made_by_organizer", true);
  }

  if (publishedState === "archived" || publishedState === "draft") {
    query = query.order("start_time", { ascending: false });
  } else {
    query = query.order("start_time");
  }
  query = query.range((page - 1) * perPage, (page - 1) * perPage + perPage - 1);
  const { data, count, error } = await query;

  if (error) {
    throw error;
  }
  return { events: data || [], count };
};
